/**
 * The kicker — the design system's signature label.
 *
 * Gilroy ExtraBold at 11px, uppercase, tracked to 0.2em. It labels a card, a
 * section or a column, and it is what makes a screen read as Greenvolt rather
 * than as generic admin furniture: the DS spends its display font on chrome,
 * not only on headings.
 *
 * The optional `count` is rendered in green-dark. That is consistent with §4.2
 * rather than an exception to it — the number *is* a data value, and the label
 * beside it is not.
 *
 * **This does not render a heading.** A kicker is a visual treatment, and the
 * mockup applies it to card headings and to standalone labels alike. Where the
 * text is genuinely a section heading, pass `as="h2"` (or whichever level the
 * document outline calls for) so the treatment and the semantics can be chosen
 * independently — heading level is an outline decision, not a styling one.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 6.5.3, §4.5
 */
import { cn } from '../../lib/cn';

/** Elements a kicker may render as. Constrained so it cannot become a button. */
type KickerElement = 'span' | 'div' | 'h2' | 'h3' | 'h4';

export interface KickerProps {
  children: React.ReactNode;
  /**
   * A count shown after the label, in green-dark and tabular figures.
   * `0` renders — only `undefined` omits it, because "0 pilots" is information
   * and hiding it would be a silent lie.
   */
  count?: number | undefined;
  /** The element to render. Defaults to a `<span>`, which carries no outline. */
  as?: KickerElement;
  className?: string;
}

const KICKER_BASE =
  'flex items-center gap-2 font-display text-[11px] font-extrabold ' +
  'tracking-[0.2em] text-navy uppercase';

export function Kicker({
  children,
  count,
  as: Component = 'span',
  className,
}: KickerProps): React.JSX.Element {
  return (
    <Component className={cn(KICKER_BASE, className)}>
      {children}
      {count !== undefined && (
        <span className="text-green-dark tabular-nums">{count}</span>
      )}
    </Component>
  );
}
